import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExternalLink, RotateCcw, ShieldX, Ban, Trash2 } from "lucide-react";

// Intake Log merged in: the block list of rejected / junked leads, with undo.
function BlockList() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/suppressions").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const undo = async (r) => {
    if (!window.confirm(`Take “${r.project_name || r.company || "this item"}” off the block list?`)) return;
    try { await api.post(`/suppressions/${r.suppression_id}/undo`); toast.success("Removed from the block list"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
  };
  if (!rows.length) return null;
  return (
    <div className="pt-4" data-testid="bd-blocklist">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Block list — rejected and junked leads ({rows.length})</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <Card key={r.suppression_id || i} className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2 p-3" data-testid={`bd-block-${r.suppression_id || i}`}>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full shrink-0 ${r.deleted ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
              {r.deleted ? <><Trash2 className="w-2.5 h-2.5" /> Junked</> : <><Ban className="w-2.5 h-2.5" /> Rejected</>}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-black truncate">{r.project_name || "Untitled"}{r.company ? ` · ${r.company}` : ""}</div>
              <div className="text-xs text-slate-500 truncate">{r.domain || "unknown sender"}{r.reason ? ` · “${r.reason}”` : ""}{r.by ? ` · by ${r.by}` : ""}{r.created_at ? ` · ${fmtDate(r.created_at)}` : ""}</div>
            </div>
            <Button size="sm" variant="outline" disabled={!r.suppression_id} onClick={() => undo(r)} className="shrink-0" data-testid={`bd-block-undo-${r.suppression_id || i}`}><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Undo</Button>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Section 5: the screened-out list — searchable, never deleted, one click "this was a real lead".
export function ScreenedList({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = (query = q) => api.get("/bd/screened-out", { params: { q: query } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (s) => {
    const reason = window.prompt("Why was this a real lead? (optional — recorded as a correction)") ?? null;
    if (reason === null) return;
    try { await api.post(`/bd/screened-out/${s.screened_id}/restore`, { reason }); toast.success("Moved to the tray"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sender, company, project, reason…" className="max-w-md" data-testid="bd-screened-search" />
        <Button type="submit" variant="outline" data-testid="bd-screened-search-btn">Search</Button>
      </form>
      {rows === null ? <div className="text-sm text-slate-400 py-12 text-center">Loading…</div> : !rows.length ? (
        <Card className="p-12 text-center" data-testid="bd-screened-empty">
          <ShieldX className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-600">Nothing screened out yet.</div>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="bd-screened-list">
          {rows.map((s) => (
            <Card key={s.screened_id} className={`flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2 p-3 ${s.restored ? "opacity-60" : ""}`} data-testid={`bd-screened-${s.screened_id}`}>
              <div className="text-xs text-slate-400 w-20 shrink-0">{fmtDate(s.date)}</div>
              <div className="flex-1 min-w-0 basis-40">
                <div className="text-sm font-medium text-black truncate">{s.project || s.subject || "Untitled"}{s.company ? <span className="text-slate-500 font-normal"> · {s.company}</span> : null}</div>
                <div className="text-xs text-slate-500 truncate">{s.from_email}{s.platform ? ` · via ${s.platform}` : ""} · <span className="text-slate-700">{s.reason}</span></div>
              </div>
              <a href={s.gmail_link} target="_blank" rel="noreferrer" className="text-xs text-[#003E87] hover:underline inline-flex items-center gap-1 shrink-0" data-testid={`bd-screened-email-${s.screened_id}`}><ExternalLink className="w-3 h-3" /> Email</a>
              {s.restored ? <span className="text-[11px] text-emerald-700 shrink-0">Restored</span> : (
                <Button size="sm" variant="outline" onClick={() => restore(s)} className="shrink-0" data-testid={`bd-screened-restore-${s.screened_id}`}><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> This was a real lead</Button>
              )}
            </Card>
          ))}
        </div>
      )}
      <BlockList />
    </div>
  );
}
